// Public privacy policy for the Eizel service (web app, iOS app, Share
// Extension, and browser extension). Served at /privacy with no auth —
// referenced by the App Store listing and the Chrome Web Store listing.
// The data described here must stay consistent with the iOS privacy
// nutrition labels (Eizel/PrivacyInfo.xcprivacy): Email Address and Other
// User Content, both used only for App Functionality, neither for tracking.
export default function PrivacyPage() {
  return (
    <>
      <header className="shared-article-header">
        <a href="https://eizel.co" className="shared-article-logo">
          <img src="/favicon.svg" alt="Eizel" width={22} height={22} />
          <span>Eizel</span>
        </a>
      </header>
      <main className="page">
        <h1 className="article-detail-title">Privacy Policy</h1>
        <hr className="article-detail-divider" />
        <div className="article-content article-content--plaintext">
          <p>
            <em>Last updated: July 13, 2026</em>
          </p>
          <p>
            Eizel is a personal read-later service. It lets you save articles and links to your own
            library and read them later on the web or in the Eizel iOS app. This policy explains
            what data Eizel collects, why, and how it is handled. It applies to the Eizel website,
            the Eizel iOS app, the Eizel Share Extension, and the Eizel Saver browser extension
            (together, &ldquo;Eizel&rdquo;).
          </p>
          <p>
            The short version: Eizel collects only what it needs to run your account and save your
            content. It does not track you, show ads, run analytics, or sell or share your data.
          </p>

          <h2>What we collect</h2>
          <ul>
            <li>
              <strong>Your email address.</strong> Used to create and sign in to your account, and
              to let you save content by forwarding it to <strong>save@eizel.co</strong> from an
              address you&rsquo;ve verified. You may register up to three email addresses.
            </li>
            <li>
              <strong>Content you save.</strong> The links, articles, and any attachments you choose
              to save, plus tags and read/favorite status you add. This is your library content and
              is visible only to you. To build your saved articles, Eizel fetches the public web
              pages at the URLs you save so it can show you a clean reading view.
            </li>
            <li>
              <strong>Authentication tokens.</strong> When you sign in, sign-in tokens are stored on
              your device (in the browser, or in the iOS Keychain and the app&rsquo;s App Group) so
              you stay signed in. They are sent only to Eizel and to Auth0 to authenticate your
              requests.
            </li>
            <li>
              <strong>Device token for notifications (iOS only).</strong> If you use the iOS app, an
              Apple push token is stored so Eizel can notify your device when new content is ready
              to download for offline reading. It is used for no other purpose.
            </li>
          </ul>
          <p>
            Eizel does <strong>not</strong> collect your location, contacts, browsing history,
            advertising identifiers, or any usage-tracking data.
          </p>

          <h2>How your sign-in works</h2>
          <p>
            Eizel uses <strong>Auth0</strong> to handle authentication. You can sign in with email
            and password, with Google, or with Sign in with Apple. If you use Sign in with Apple and
            choose &ldquo;Hide My Email,&rdquo; Apple gives Eizel a private relay address instead of
            your real email; Eizel treats that relay address as your account identifier and never
            receives your underlying Apple email.
          </p>

          <h2>Who we share data with</h2>
          <p>
            Eizel does not sell your data and does not share it with third parties for advertising
            or marketing. Data is processed only by the service providers needed to operate Eizel:
          </p>
          <ul>
            <li>
              <strong>Auth0</strong> — authenticates your sign-in and manages your session tokens.
            </li>
            <li>
              <strong>Amazon Web Services (AWS)</strong> — hosts the Eizel backend and stores your
              account and saved content. Email you send to <strong>save@eizel.co</strong> is
              received and processed through AWS to save the linked content to your library. iOS
              push notifications are delivered via Apple&rsquo;s Push Notification service.
            </li>
          </ul>

          <h2>Data retention and deletion</h2>
          <p>
            Your saved articles and attachments remain in your account until you delete them, which
            you can do at any time from the app. You can remove a registered email address from
            Settings.
          </p>
          <p>
            You can permanently delete your entire account from the iOS app (
            <strong>Settings &rarr; Remove Account</strong>). Deleting your account permanently
            removes your saved articles, attachments, and registered email addresses from Eizel;
            this cannot be undone. Locally stored sign-in tokens are removed when you sign out or
            uninstall.
          </p>

          <h2>Children</h2>
          <p>Eizel is not directed to children and is not intended for use by anyone under 13.</p>

          <h2>Changes to this policy</h2>
          <p>
            If this policy changes, the &ldquo;Last updated&rdquo; date above will change. Material
            changes will be reflected on this page.
          </p>

          <h2>Contact</h2>
          <p>
            Questions about this policy or your data:{' '}
            <a href="mailto:support@eizel.co">support@eizel.co</a>
          </p>
        </div>
        <div className="shared-article-footer">
          <a href="https://eizel.co">eizel.co</a>
        </div>
      </main>
    </>
  )
}
